// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook module for managing customer reports with sorting capabilities.
 * Provides a convenient interface for fetching and sorting customer asset reports.
 */
import { Sort } from '@/constants/sort';
import {
  CustomerFunctionalStatusReport,
  CustomerOwnershipReport,
  getCustomerReport,
} from '@/customer/api/get-customer-report';
import { useQuery } from '@tanstack/react-query';

/**
 * Properties that can be used to sort customer reports.
 * - 'name': Sort by customer name
 * - 'count': Sort by total asset count
 */
type CustomerReportSortProperty = 'name' | 'count';

/**
 * Type definition for customer report sorting configuration.
 * Extends the base Sort type with CustomerReportSortProperty options.
 */
export type CustomerReportSort = Sort<CustomerReportSortProperty>;

/**
 * Union type representing either a functional status report or ownership report.
 * Used internally for consistent sorting implementation.
 */
type Report = CustomerFunctionalStatusReport | CustomerOwnershipReport;

/**
 * React hook for fetching and managing customer reports with sorting capabilities.
 * Provides both functional status and ownership reports for customers with assets.
 *
 * Features:
 * - Automatic data fetching and caching via React Query
 * - Sorting by customer name or total asset count
 * - Filtering to show only customers with assets (count > 0)
 *
 * @param sort - Configuration object for sorting the reports
 * @param sort.property - Property to sort by ('name' or 'count')
 * @param sort.direction - Sort direction ('asc' or 'desc')
 *
 * @returns Object containing:
 * - functionalStatusData: Sorted array of functional status reports
 * - ownershipData: Sorted array of ownership reports
 * - Query status information (loading, error, etc.)
 */
export function useCustomerReport(sort: CustomerReportSort) {
  // Set up React Query for data fetching
  const query = useQuery({
    queryKey: ['customer-reports'],
    queryFn: getCustomerReport,
  });

  /**
   * Comparison function for sorting reports based on the current sort configuration.
   * Handles both name and count sorting in ascending or descending order.
   *
   * @param report - First report to compare
   * @param other - Second report to compare
   * @returns Negative if report should come first, positive if other should come first
   */
  const compare = (report: Report, other: Report) => {
    switch (sort.property) {
      case 'name':
        return sort.direction === 'asc'
          ? report.customer.name.localeCompare(other.customer.name)
          : other.customer.name.localeCompare(report.customer.name);

      case 'count':
        return sort.direction === 'asc'
          ? report.totalCount - other.totalCount
          : other.totalCount - report.totalCount;
    }
  };

  // Process and sort functional status reports
  const functionalStatusData =
    query.data?.functional
      .slice()
      .filter((report) => report.totalCount > 0)
      .sort(compare) ?? [];

  // Process and sort ownership reports
  const ownershipData =
    query.data?.ownership
      .slice()
      .filter((report) => report.totalCount > 0)
      .sort(compare) ?? [];

  return { ...query, functionalStatusData, ownershipData };
}
